import { useCallback, useEffect, useRef, useState } from "react"
import {
  armConfirmDelay,
  cancelConfirmDelay,
  fireConfirmDelayNow,
  getConfirmDelaySnapshot,
  subscribeConfirmDelay,
  updateConfirmDelayOnFire,
  type ConfirmDelaySnapshot,
} from "@/lib/confirm-delay-registry"

export const DEFAULT_CONFIRM_DELAY_SECONDS = 2

/**
 * Armed cancel CTA: Button `outline` base (same as Upload neighbors).
 * Dark only: accent border instead of border-strong. No fill / no custom hex.
 */
export const CONFIRM_DELAY_ARMED_BUTTON_CLASS = "dark:!border-ui-accent dark:hover:!border-ui-accent-hover"

/** Flatten trailing split shadow while armed (light only — dark keeps accent shadow/ring). */
export const CONFIRM_DELAY_ARMED_TRAILING_WRAP_CLASS = "light:[&_button]:!shadow-none"

type UseConfirmDelayActionOptions = {
  /**
   * Stable registry id across SPA remounts (`confirmDelayKey(...)`).
   * Same key for generate/regenerate of one action.
   * Changing key resubscribes only — previous id keeps running and fires its arm-time snapshot
   * (not the live handler of the new key).
   */
  persistKey: string
  /** Seconds before `onFire`. Default 2; `0` = fire immediately on `request`. */
  seconds?: number
  /** When true, clears any active countdown. */
  disabled?: boolean
  /** Return `false` if work did not start (no busy) so pending bridge clears. */
  onFire: () => void | boolean | Promise<void | boolean>
  /** Gate before starting countdown. Return false to abort. */
  onBeforeArm?: () => boolean | Promise<boolean>
}

export type ConfirmDelayAction = {
  countdownLeft: number | null
  isArmed: boolean
  /** True after fire until parent sets `disabled` (busy) — bridges countdown → loader without idle flash. */
  isPending: boolean
  /** Idle → start countdown (or fire if seconds≤0); armed → cancel. */
  request: () => void
  cancel: () => void
  /** Skip delay and call `onFire` immediately (e.g. agent). */
  fireNow: () => void
}

function snapToState(snap: ConfirmDelaySnapshot) {
  return {
    countdownLeft: snap.phase === "armed" ? snap.remaining : null,
    isPending: snap.phase === "pending",
  }
}

export function useConfirmDelayAction({
  persistKey,
  seconds = DEFAULT_CONFIRM_DELAY_SECONDS,
  disabled = false,
  onFire,
  onBeforeArm,
}: UseConfirmDelayActionOptions): ConfirmDelayAction {
  const id = persistKey

  const armingRef = useRef(false)
  const onFireRef = useRef(onFire)
  onFireRef.current = onFire
  const onBeforeArmRef = useRef(onBeforeArm)
  onBeforeArmRef.current = onBeforeArm

  const [ui, setUi] = useState(() => snapToState(getConfirmDelaySnapshot(id)))

  useEffect(() => {
    // Pass onFire by value (not a live-ref wrapper) so each registry id keeps its own snapshot.
    updateConfirmDelayOnFire(id, onFire)
  }, [id, onFire])

  // Subscribe for UI; unmount must NOT cancel — timer + onFire live in the registry.
  // Changing persistKey resubscribes only; previous id keeps running with its arm-time onFire.
  useEffect(() => {
    return subscribeConfirmDelay(id, (snap) => {
      setUi(snapToState(snap))
    })
  }, [id])

  const cancel = useCallback(() => {
    cancelConfirmDelay(id)
  }, [id])

  useEffect(() => {
    if (disabled) cancel()
  }, [disabled, cancel])

  const fireNow = useCallback(() => {
    fireConfirmDelayNow(id, onFireRef.current)
  }, [id])

  const request = useCallback(() => {
    if (armingRef.current || ui.isPending) return

    // Allow cancel while armed even if `disabled` (e.g. model flipped off mid-countdown).
    if (ui.countdownLeft != null) {
      cancel()
      return
    }

    if (disabled) return

    if (seconds <= 0) {
      fireConfirmDelayNow(id, onFireRef.current)
      return
    }

    armingRef.current = true
    void (async () => {
      try {
        const before = onBeforeArmRef.current
        if (before && !(await before())) return
        // Snapshot at arm — not `() => onFireRef.current()` (would retarget after persistKey change).
        armConfirmDelay(id, seconds, onFireRef.current)
      } finally {
        armingRef.current = false
      }
    })()
  }, [disabled, ui.isPending, ui.countdownLeft, seconds, cancel, id])

  return {
    countdownLeft: ui.countdownLeft,
    isArmed: ui.countdownLeft != null,
    isPending: ui.isPending,
    request,
    cancel,
    fireNow,
  }
}
